import { species as speciesInfo, text } from "@redchain/gen1-data";
import { bcd } from "./bcd.js";
import { slice, u16be, u8 } from "./bytes.js";
import { boxesInitialised as boxesInitialisedFlag, validateChecksums } from "./checksum.js";
import { SaveFormatError, type SaveIssue } from "./errors.js";
import { BAG_LIST, PC_LIST, readItemList, readPockets } from "./items.js";
import {
  BOX,
  BOX_NUM_MASK,
  BOX_STRUCT_LENGTH,
  EVENT_FLAG_BYTES,
  F,
  MONS_PER_BOX,
  NAME_LENGTH,
  NUM_BOXES,
  PARTY,
  PARTY_LENGTH,
  PARTY_STRUCT_LENGTH,
  POKEDEX_BYTES,
  S,
  SRAM_SIZE,
  TERMINATOR,
  TEXT_TERMINATOR,
  storedBoxOffset,
} from "./layout.js";
import { decodeBoxStruct, decodePartyStruct } from "./mon.js";
import type { MonLocation, ParsedSave, SavedMon } from "./types.js";

export interface ParseOptions {
  /**
   * When true, every §4.7 invariant throws a SaveFormatError (species list mismatch,
   * invalid species, item list anomalies). When false (default) only counts that make
   * the save unreadable throw; the rest are reported in `issues`, so glitch Pokémon
   * (local-only, §13.1) still parse.
   */
  strict?: boolean;
}

function decodeText(b: Uint8Array): string {
  return text.decode(b);
}

function makeMon(
  location: MonLocation,
  structBytes: Uint8Array,
  isParty: boolean,
  otName: Uint8Array,
  nickname: Uint8Array,
): SavedMon {
  const party = isParty ? decodePartyStruct(structBytes) : undefined;
  return {
    location,
    box: party ? decodeBoxStruct(party.raw44) : decodeBoxStruct(structBytes),
    ...(party ? { party } : {}),
    nickname,
    otName,
    nicknameText: decodeText(nickname),
    otNameText: decodeText(otName),
  };
}

interface BlockSpec {
  base: number;
  capacity: number;
  structLength: number;
  rel: { count: number; species: number; mons: number; ot: number; nicks: number };
}

/** Reads a party or box block; `locate(slot)` builds each mon's location. */
function readBlock(
  sram: Uint8Array,
  spec: BlockSpec,
  locate: (slot: number) => MonLocation,
  issues: SaveIssue[],
  strict: boolean,
): SavedMon[] {
  const { base, capacity, structLength, rel } = spec;
  const count = u8(sram, base + rel.count);
  if (count > capacity) throw new SaveFormatError(`count ${count} exceeds ${capacity}`, base + rel.count);
  const report = (offset: number, message: string) => {
    if (strict) throw new SaveFormatError(message, offset);
    issues.push({ offset, message });
  };
  const listEnd = base + rel.species + count;
  if (u8(sram, listEnd) !== TERMINATOR) report(listEnd, `species list not terminated at index ${count}`);
  const mons: SavedMon[] = [];
  for (let i = 0; i < count; i++) {
    const structOff = base + rel.mons + i * structLength;
    const structBytes = slice(sram, structOff, structLength);
    const listSpecies = u8(sram, base + rel.species + i);
    const structSpecies = u8(structBytes, 0);
    if (listSpecies !== structSpecies) {
      report(base + rel.species + i, `species list entry 0x${listSpecies.toString(16)} != struct species 0x${structSpecies.toString(16)}`);
    }
    if (speciesInfo(structSpecies) === null) report(structOff, `invalid species 0x${structSpecies.toString(16)}`);
    mons.push(
      makeMon(
        locate(i),
        structBytes,
        structLength === PARTY_STRUCT_LENGTH,
        slice(sram, base + rel.ot + i * NAME_LENGTH, NAME_LENGTH),
        slice(sram, base + rel.nicks + i * NAME_LENGTH, NAME_LENGTH),
      ),
    );
  }
  return mons;
}

export function partySpec(): BlockSpec {
  return { base: S.sPartyData, capacity: PARTY_LENGTH, structLength: PARTY_STRUCT_LENGTH, rel: PARTY };
}

export function boxSpec(base: number): BlockSpec {
  return { base, capacity: MONS_PER_BOX, structLength: BOX_STRUCT_LENGTH, rel: BOX };
}

/** SRAM offset of the block holding box `box` (sCurBoxData for the current box). */
export function boxBlockOffset(sram: Uint8Array, box: number): number {
  return box === (u8(sram, F.currentBoxNum) & BOX_NUM_MASK) ? S.sCurBoxData : storedBoxOffset(box);
}

export function parseSave(sram: Uint8Array, opts: ParseOptions = {}): ParsedSave {
  if (sram.length !== SRAM_SIZE) throw new SaveFormatError(`SRAM must be ${SRAM_SIZE} bytes, got ${sram.length}`, 0);
  const strict = opts.strict ?? false;
  const issues: SaveIssue[] = [];

  const curBoxByte = u8(sram, F.currentBoxNum);
  const currentBox = curBoxByte & BOX_NUM_MASK;
  if (currentBox >= NUM_BOXES) throw new SaveFormatError(`current box ${currentBox} out of range`, F.currentBoxNum);
  const initialised = boxesInitialisedFlag(sram);

  const party = readBlock(sram, partySpec(), (slot) => ({ kind: "party", slot }), issues, strict);

  const boxCounts = new Array<number>(NUM_BOXES).fill(0);
  const boxMons: SavedMon[] = [];
  for (let box = 0; box < NUM_BOXES; box++) {
    if (box !== currentBox && !initialised) continue;
    const base = box === currentBox ? S.sCurBoxData : storedBoxOffset(box);
    const mons = readBlock(sram, boxSpec(base), (slot) => ({ kind: "box", box, slot }), issues, strict);
    boxCounts[box] = mons.length;
    boxMons.push(...mons);
  }

  const dayCareInUse = u8(sram, F.dayCareInUse) !== 0;
  const dayCare: SavedMon[] = [];
  if (dayCareInUse) {
    const structBytes = slice(sram, F.dayCareMon, BOX_STRUCT_LENGTH);
    const sp = u8(structBytes, 0);
    if (speciesInfo(sp) === null) {
      if (strict) throw new SaveFormatError(`invalid Day Care species 0x${sp.toString(16)}`, F.dayCareMon);
      issues.push({ offset: F.dayCareMon, message: `invalid Day Care species 0x${sp.toString(16)}` });
    }
    dayCare.push(
      makeMon({ kind: "daycare" }, structBytes, false, slice(sram, F.dayCareMonOT, NAME_LENGTH), slice(sram, F.dayCareMonName, NAME_LENGTH)),
    );
  }

  const itemIssues: SaveIssue[] = [];
  const bag = readItemList(sram, BAG_LIST, itemIssues);
  const pcItems = readItemList(sram, PC_LIST, itemIssues);
  if (strict && itemIssues[0]) throw new SaveFormatError(itemIssues[0].message, itemIssues[0].offset);
  issues.push(...itemIssues);

  const playerNameRaw = slice(sram, F.playerName, NAME_LENGTH);
  const rivalNameRaw = slice(sram, F.rivalName, NAME_LENGTH);

  return {
    playerName: decodeText(playerNameRaw),
    playerNameRaw,
    playerId: u16be(sram, F.playerId),
    money: bcd.read(slice(sram, F.money, 3)),
    coins: bcd.read(slice(sram, F.coins, 2)),
    badges: u8(sram, F.badges),
    playTime: {
      hours: u8(sram, F.playTimeHours),
      minutes: u8(sram, F.playTimeMinutes),
      seconds: u8(sram, F.playTimeSeconds),
      frames: u8(sram, F.playTimeFrames),
      maxed: u8(sram, F.playTimeMaxed) !== 0,
    },
    pokedexOwned: slice(sram, F.pokedexOwned, POKEDEX_BYTES),
    pokedexSeen: slice(sram, F.pokedexSeen, POKEDEX_BYTES),
    currentBox,
    bag,
    pcItems,
    pockets: readPockets(sram),
    mons: [...party, ...boxMons, ...dayCare],
    partyCount: party.length,
    boxCounts,
    dayCareInUse,
    dayCarePaid: bcd.isValid(slice(sram, F.dayCarePaid, 4)) ? bcd.read(slice(sram, F.dayCarePaid, 4)) : null,
    trainedLevels: u16be(sram, F.trainedLevels),
    checksumsValid: validateChecksums(sram),
    mapId: u8(sram, F.curMap),
    rivalNameRaw,
    rivalName: decodeText(rivalNameRaw),
    options: u8(sram, F.options),
    boxesInitialised: initialised,
    hofCount: u8(sram, F.numHoFTeams),
    eventFlags: slice(sram, F.eventFlags, EVENT_FLAG_BYTES),
    issues,
  };
}

/** True if the game would offer NEW GAME only: no 0x50 in sPlayerName or a bad main checksum. */
export function isBlankSram(sram: Uint8Array): boolean {
  if (sram.length !== SRAM_SIZE) throw new RangeError(`SRAM must be ${SRAM_SIZE} bytes, got ${sram.length}`);
  let hasTerminator = false;
  for (let i = 0; i < NAME_LENGTH; i++) if (sram[F.playerName + i] === TEXT_TERMINATOR) hasTerminator = true;
  if (!hasTerminator) return true;
  return !validateChecksums(sram).main;
}
